import { useCallback, useEffect, useState } from "react";

// Offset do cabeçalho fixo: sem ele a âncora para exatamente embaixo da subnav.
const SCROLL_OFFSET = 68;

// Marca qual âncora está sendo lida. IntersectionObserver seria o reflexo
// natural, mas ele responde "entrou/saiu da viewport" — com vários headings
// visíveis ao mesmo tempo, qual deles é o corrente continua ambíguo. Ler as
// posições no scroll responde exatamente a pergunta certa: o último heading que
// já passou pelo topo.
export function useScrollSpy(ids: string[]): string | null {
  const [active, setActive] = useState<string | null>(null);
  const key = ids.join("|");

  useEffect(() => {
    if (!ids.length) {
      setActive(null);
      return undefined;
    }

    let frame: number | null = null;
    const measure = () => {
      frame = null;
      let current: string | null = ids[0] ?? null;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= SCROLL_OFFSET + 22) current = id;
      }
      setActive(current);
    };

    const onScroll = () => {
      if (frame === null) frame = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    // A página cresce depois de montada — um diagrama do Mermaid chega por
    // import dinâmico e empurra tudo abaixo dele. Isso não dispara `scroll` nem
    // `resize` da janela, então sem observar o documento a medição fica presa no
    // layout de antes e o índice destaca a seção errada.
    const observer = new ResizeObserver(onScroll);
    observer.observe(document.documentElement);

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return active;
}

// Salta para a âncora de um link compartilhado — o `#secao` que chega na URL.
//
// O salto nativo do navegador não serve aqui: ele acontece uma vez, no load, e
// nessa hora o conteúdo ainda não existe. Guia carrega o MDX por import
// dinâmico, referência espera o fetch do spec; nos dois casos o heading entra
// no DOM depois, o navegador não acha o id e desiste **em silêncio**. A página
// fica no topo e o link parece quebrado, que foi como isto apareceu.
//
// Daí `ready`: quem chama diz quando o conteúdo montou. Mas pular uma vez não
// basta, e contar frames também não. O que está ACIMA da âncora continua
// crescendo depois do salto — o Mermaid é um import dinâmico de ~1 MB, e cada
// diagrama que renderiza empurra o heading para baixo do ponto onde já paramos.
//
// A primeira versão disto remedia por ~2s e parava quando a posição ficasse
// estável por três frames. Passava na máquina de quem escreveu, onde o chunk do
// Mermaid vem do disco, e falhava em produção, onde ele vem pela rede e chega
// depois da janela fechar: o leitor caía 1311px acima da seção pedida.
//
// Então a correção não é cronometrada, é reativa: um ResizeObserver no
// documento reancorá a página toda vez que a altura mudar, até a pessoa
// interagir. Aí o salto para — completá-lo depois disso seria tomar dela o
// controle da página.
export function useHashScroll(hash: string, ready: boolean): void {
  useEffect(() => {
    if (!ready || !hash) return undefined;

    const id = decodeURIComponent(hash.slice(1));
    let frame: number | null = null;
    let restantes = 60; // procura o heading por ~1s antes de desistir
    let cancelado = false;

    const desistir = () => {
      cancelado = true;
    };
    const opts = { passive: true, once: true };
    window.addEventListener("wheel", desistir, opts);
    window.addEventListener("touchstart", desistir, opts);
    window.addEventListener("mousedown", desistir, opts); // arrastar a barra de rolagem
    window.addEventListener("keydown", desistir, { once: true });

    const ancorar = () => {
      if (cancelado) return true;
      const el = document.getElementById(id);
      if (!el) return false;
      const delta = el.getBoundingClientRect().top - SCROLL_OFFSET;
      if (Math.abs(delta) > 1) window.scrollTo({ top: window.scrollY + delta, behavior: "instant" });
      return true;
    };

    // O heading pode não existir ainda por alguns frames, mesmo com `ready`.
    const procurar = () => {
      frame = null;
      if (ancorar() || --restantes <= 0) return;
      frame = requestAnimationFrame(procurar);
    };
    frame = requestAnimationFrame(procurar);

    // Enquanto a página crescer, reancorá. Sem teto de tempo de propósito: o que
    // encerra é a pessoa mexer, não um cronômetro que pode fechar antes do
    // conteúdo chegar — foi exatamente esse cronômetro que falhou em produção.
    const observer = new ResizeObserver(() => {
      if (cancelado) observer.disconnect();
      else ancorar();
    });
    observer.observe(document.documentElement);

    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("wheel", desistir);
      window.removeEventListener("touchstart", desistir);
      window.removeEventListener("mousedown", desistir);
      window.removeEventListener("keydown", desistir);
    };
  }, [hash, ready]);
}

export function useScrollToAnchor(): (id: string) => void {
  return useCallback((id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
    window.scrollTo({ top: y, behavior: "smooth" });
    // Mantém a URL compartilhável sem disparar o salto nativo do navegador.
    window.history.replaceState(null, "", `#${id}`);
  }, []);
}
